import { getAuthToken } from '../services/auth/tokenStorage';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { API_URL } from '../config';
import { subscriptionLogger } from '../utils/logger';

// Subscription tiers
export type SubscriptionTier = 'trial' | 'expired' | 'letter' | 'basic' | 'plus' | 'premium';

export type SubscriptionStatus = 'active' | 'expired' | 'cancelled' | 'grace_period';

// Storage types - determines where documents are stored
// NONE: No document storage (trial/basic tiers)
// LOCAL: Local device storage only ($59 Plus tier)
// CLOUD: Cloud (S3) storage with local cache ($99 Premium tier)
export type StorageType = 'none' | 'local' | 'cloud';

export interface SubscriptionInfo {
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  isActive: boolean;
  canEditData: boolean;
  canViewOwnData: boolean;  // Whether user can view their own data (requires active subscription)
  canUploadDocuments: boolean;
  canUploadToCloud: boolean;  // Whether user can upload to cloud storage (Premium tier only)
  storageType: StorageType;  // NONE, LOCAL ($59 Plus), or CLOUD ($99 Premium)
  isSuperUser: boolean;
  trialDaysRemaining: number | null;
  subscriptionEndDate: string | null;
  /** False once the plan is set not to renew; absent on older API builds. */
  subscriptionAutoRenew?: boolean;
  subscriptionPlatform?: string | null;  // 'apple' | 'google' | 'stripe' | 'promo' | 'manual'
  billingInterval?: 'monthly' | 'yearly' | null;  // derived server-side from the product or price
  storageUsedBytes: number;
  storageLimitBytes: number;
  discountTier: string | null;
  hasStartedOwnTrial: boolean;  // Whether user has explicitly started their own trial
  testDriveAvailable: boolean;  // 14-day vault test drive still unused and offerable
  isFoundingMember: boolean;
  // Set by the backend when the user is an active non-owner member of an
  // active family group. Use this (not subscriptionTier) to display
  // "You're part of X's Family Plan" instead of the underlying tier.
  isFamilyMember: boolean;
  familyOwnerName: string | null;
  message: string;
}

interface SubscriptionContextType {
  subscription: SubscriptionInfo | null;
  isLoading: boolean;
  canEdit: boolean;
  canEditData: boolean;  // alias of canEdit, matching the server field name
  canViewOwnData: boolean;  // Whether user can view their own data (requires active subscription)
  canUploadDocuments: boolean;
  canUploadToCloud: boolean;  // Whether user can upload to cloud storage (Premium tier only)
  storageType: StorageType;  // NONE, LOCAL ($59 Plus), or CLOUD ($99 Premium)
  isSuperUser: boolean;
  trialDaysRemaining: number | null;
  currentTier: SubscriptionTier;
  hasStartedOwnTrial: boolean;  // Whether user has explicitly started their own trial
  testDriveAvailable: boolean;  // 14-day vault test drive still unused and offerable
  isFoundingMember: boolean;
  isFamilyMember: boolean;  // True if the user is a non-owner member of an active family group
  familyOwnerName: string | null;
  foundingStatus: { available: boolean; slotsRemaining: number; totalSlots: number } | null;
  fetchFoundingStatus: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  applyPromoCode: (code: string) => Promise<{ success: boolean; message: string }>;
  startFreeTrial: () => Promise<{ success: boolean; message: string }>;
  showUpgradePrompt: boolean;
  setShowUpgradePrompt: (show: boolean) => void;
  storageUsedBytes: number;
  storageLimitBytes: number;
  storagePercentUsed: number;
}

// Default subscription for unauthenticated users - restrictive by default
// This is used when there's no token (user not logged in)
const defaultSubscription: SubscriptionInfo = {
  subscriptionTier: 'expired',
  subscriptionStatus: 'expired',
  isActive: false,
  canEditData: false,
  canViewOwnData: false,
  canUploadDocuments: false,
  canUploadToCloud: false,
  storageType: 'none',
  isSuperUser: false,
  trialDaysRemaining: null,
  subscriptionEndDate: null,
  storageUsedBytes: 0,
  storageLimitBytes: 0,
  discountTier: null,
  hasStartedOwnTrial: false,
  testDriveAvailable: false,
  isFoundingMember: false,
  isFamilyMember: false,
  familyOwnerName: null,
  message: 'Please log in to access your data',
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [foundingStatus, setFoundingStatus] = useState<{
    available: boolean;
    slotsRemaining: number;
    totalSlots: number;
  } | null>(null);
  const lastTokenRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchFoundingStatus = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/api/subscription/founding-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setFoundingStatus(data);
      }
    } catch (error) {
      subscriptionLogger.warn('Failed to fetch founding status:', error);
    }
  }, []);

  const fetchSubscriptionStatus = useCallback(async () => {
    console.log('🔄 [SubscriptionContext] fetchSubscriptionStatus: Starting fetch...');
    subscriptionLogger.info('fetchSubscriptionStatus: Starting fetch...');
    try {
      const token = await getAuthToken();
      console.log('🔄 [SubscriptionContext] Token exists:', !!token);
      subscriptionLogger.info('fetchSubscriptionStatus: Token exists:', !!token);
      if (!token) {
        console.log('🔄 [SubscriptionContext] No token, using default');
        subscriptionLogger.info('fetchSubscriptionStatus: No token, using default');
        setSubscription(defaultSubscription);
        setIsLoading(false);
        return;
      }

      console.log('🔄 [SubscriptionContext] Fetching from:', `${API_URL}/api/subscription/status`);
      subscriptionLogger.info('fetchSubscriptionStatus: Fetching from', `${API_URL}/api/subscription/status`);
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      console.log('🔄 [SubscriptionContext] Response status:', response.status);
      subscriptionLogger.info('fetchSubscriptionStatus: Response status:', response.status);

      if (!response.ok) {
        // If the endpoint doesn't exist yet, use default
        console.log('🔄 [SubscriptionContext] Response NOT OK, using default');
        subscriptionLogger.warn('Subscription status fetch failed, using default');
        setSubscription(defaultSubscription);
        setIsLoading(false);
        return;
      }

      const data: SubscriptionInfo = await response.json();
      console.log('🔄 [SubscriptionContext] *** RECEIVED DATA ***:', JSON.stringify(data, null, 2));
      console.log('🔄 [SubscriptionContext] *** canViewOwnData from API ***:', data.canViewOwnData);
      subscriptionLogger.info('fetchSubscriptionStatus: Received data:', JSON.stringify(data));
      subscriptionLogger.info('fetchSubscriptionStatus: canViewOwnData from API:', data.canViewOwnData);
      setSubscription(data);

      // Show upgrade prompt if trial is expired and user can't edit
      if (!data.canEditData && !data.isSuperUser) {
        console.log('🔄 [SubscriptionContext] Should show upgrade prompt (canEditData=false, isSuperUser=false)');
        subscriptionLogger.info('fetchSubscriptionStatus: Showing upgrade prompt (canEditData=false, isSuperUser=false)');
        setShowUpgradePrompt(true);
      }
    } catch (error) {
      console.log('🔄 [SubscriptionContext] ERROR:', error);
      subscriptionLogger.warn('Subscription status fetch error:', error);
      // Use default for development
      setSubscription(defaultSubscription);
    } finally {
      console.log('🔄 [SubscriptionContext] Finished, setting isLoading=false');
      setIsLoading(false);
    }
  }, []);

  const applyPromoCode = useCallback(async (code: string): Promise<{ success: boolean; message: string }> => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_URL}/api/subscription/apply-promo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh subscription status after applying code
        await fetchSubscriptionStatus();
        return { success: true, message: data.message || 'Promo code applied successfully!' };
      } else {
        return { success: false, message: data.message || 'Invalid promo code' };
      }
    } catch (error) {
      subscriptionLogger.error('Apply promo code error:', error);
      return { success: false, message: 'Failed to apply promo code' };
    }
  }, [fetchSubscriptionStatus]);

  const startFreeTrial = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_URL}/api/subscription/start-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh subscription status after starting trial
        await fetchSubscriptionStatus();
        return { success: true, message: data.message || 'Free trial started successfully!' };
      } else {
        return { success: false, message: data.message || 'Failed to start free trial' };
      }
    } catch (error) {
      subscriptionLogger.error('Start free trial error:', error);
      return { success: false, message: 'Failed to start free trial' };
    }
  }, [fetchSubscriptionStatus]);

  // Initial fetch
  useEffect(() => {
    fetchSubscriptionStatus();
    fetchFoundingStatus();
  }, [fetchSubscriptionStatus, fetchFoundingStatus]);

  // Poll for token changes to detect login/logout
  // This ensures we re-fetch subscription status after user logs in
  useEffect(() => {
    const checkTokenChange = async () => {
      const currentToken = await getAuthToken();

      // If token changed (login or logout), refresh subscription
      if (currentToken !== lastTokenRef.current) {
        console.log('🔄 [SubscriptionContext] Token changed, refreshing subscription...');
        console.log('🔄 [SubscriptionContext] Previous token:', lastTokenRef.current ? 'YES' : 'NO');
        console.log('🔄 [SubscriptionContext] Current token:', currentToken ? 'YES' : 'NO');
        subscriptionLogger.info('Token changed, refreshing subscription');
        lastTokenRef.current = currentToken;

        // If user just logged in (token went from null to something), fetch fresh data
        if (currentToken) {
          setIsLoading(true);
          await fetchSubscriptionStatus();
        } else {
          // User logged out, reset to default
          setSubscription(defaultSubscription);
        }
      }
    };

    // Check immediately
    checkTokenChange();

    // Poll every 2 seconds to detect token changes
    pollIntervalRef.current = setInterval(checkTokenChange, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchSubscriptionStatus]);

  // Also refresh when app comes to foreground
  useEffect(() => {
    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        const token = await getAuthToken();
        if (token) {
          console.log('🔄 [SubscriptionContext] App became active, refreshing subscription...');
          fetchSubscriptionStatus();
        }
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => subscription.remove();
  }, [fetchSubscriptionStatus]);

  // Computed values
  // IMPORTANT: Default to FALSE for permissions when subscription is null/loading
  // This prevents unauthorized access before we know the user's actual subscription status
  const canEdit = subscription?.canEditData ?? false;
  const canViewOwnData = subscription?.canViewOwnData ?? false;
  const canUploadDocuments = subscription?.canUploadDocuments ?? false;
  const canUploadToCloud = subscription?.canUploadToCloud ?? false;
  const storageType: StorageType = subscription?.storageType ?? 'none';
  const isSuperUser = subscription?.isSuperUser ?? false;
  const trialDaysRemaining = subscription?.trialDaysRemaining ?? null;
  const currentTier = subscription?.subscriptionTier ?? 'trial';
  const hasStartedOwnTrial = subscription?.hasStartedOwnTrial ?? false;
  const testDriveAvailable = (subscription as any)?.testDriveAvailable ?? false;
  const isFoundingMember = subscription?.isFoundingMember ?? false;
  const isFamilyMember = subscription?.isFamilyMember ?? false;
  const familyOwnerName = subscription?.familyOwnerName ?? null;
  const storageUsedBytes = subscription?.storageUsedBytes ?? 0;
  const storageLimitBytes = subscription?.storageLimitBytes ?? 0;
  const storagePercentUsed = storageLimitBytes > 0
    ? Math.round((storageUsedBytes / storageLimitBytes) * 100)
    : 0;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isLoading,
        canEdit,
        canEditData: canEdit,
        canViewOwnData,
        canUploadDocuments,
        canUploadToCloud,
        storageType,
        isSuperUser,
        trialDaysRemaining,
        currentTier,
        hasStartedOwnTrial,
        testDriveAvailable,
        isFoundingMember,
        isFamilyMember,
        familyOwnerName,
        foundingStatus,
        fetchFoundingStatus,
        refreshSubscription: fetchSubscriptionStatus,
        applyPromoCode,
        startFreeTrial,
        showUpgradePrompt,
        setShowUpgradePrompt,
        storageUsedBytes,
        storageLimitBytes,
        storagePercentUsed,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
